import { useEffect, useRef } from 'react';
import { useChatState } from '@/hooks/useChatState';
import { Send, ChevronDown, Loader2, Zap } from 'lucide-react';

// ─── Shared constants ───────────────────────────────────────────────────────────
const BLUE = '#6994c6';

// ─── Safely renders **bold** markdown without HTML injection ────────────────────
function renderMessageContent(content) {
  return content.split('\n').map((line, lineIdx) => {
    if (!line) return <p key={`line-${lineIdx}`} className={lineIdx > 0 ? 'mt-1' : ''}>&nbsp;</p>;
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <p key={`line-${lineIdx}`} className={lineIdx > 0 ? 'mt-1' : ''}>
        {parts.map((part, partIdx) =>
          partIdx % 2 === 1 ? <strong key={`part-${partIdx}`}>{part}</strong> : part
        )}
      </p>
    );
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: 'hsl(0,0%,72%)', animation: `bounce 1.2s ease-in-out ${i * 0.18}s infinite` }}
        />
      ))}
      <style>{`@keyframes bounce { 0%,80%,100%{transform:translateY(0);opacity:0.4} 40%{transform:translateY(-4px);opacity:1} }`}</style>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mr-2 mt-0.5" style={{ background: BLUE, minWidth: '24px' }}>
          <Zap size={11} fill="white" color="white" />
        </div>
      )}
      <div
        className="max-w-[82%] text-sm leading-relaxed px-3.5 py-2.5 rounded-2xl"
        style={isUser
          ? { background: BLUE, color: 'white', borderBottomRightRadius: '6px' }
          : { background: 'hsl(60,5%,93%)', color: 'hsl(0,0%,18%)', borderBottomLeftRadius: '6px' }}
      >
        {renderMessageContent(msg.content)}
      </div>
    </div>
  );
}

function EmptyState({ quickActions, onAction }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center">
      <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'rgba(105,148,198,0.10)' }}>
        <Zap size={18} style={{ color: BLUE }} />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">Ask anything</p>
      <p className="text-xs text-muted-foreground mb-4 max-w-[240px]">Ask about your tasks, get summaries, or generate follow-up actions</p>
      <div className="flex flex-wrap gap-2 justify-center">
        {quickActions.map(action => (
          <button
            key={action.message}
            onClick={() => onAction(action.message)}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors hover:bg-secondary"
            style={{ border: '1px solid hsl(0,0%,84%)', color: 'hsl(0,0%,38%)', background: 'white' }}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatPanel({ isOpen, messages, loading, suggestions, onSend, onClear, onClose, QUICK_ACTIONS }) {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  return (
    <div
      className="absolute bottom-full left-0 right-0 mb-2 overflow-hidden"
      style={{
        transition: 'height 0.2s ease, opacity 0.2s ease',
        height: isOpen ? '360px' : '0px',
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
        borderRadius: '16px',
      }}
    >
      <div className="h-full flex flex-col" style={{ background: 'white', border: '1px solid hsl(0,0%,86%)', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.10)', overflow: 'hidden' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: '1px solid hsl(0,0%,91%)' }}>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: BLUE }} />
            <span className="text-sm font-medium text-foreground">Action AI</span>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button onClick={onClear} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded transition-colors">Clear</button>
            )}
            <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors">
              <ChevronDown size={16} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {messages.length === 0
            ? <EmptyState quickActions={QUICK_ACTIONS} onAction={onSend} />
            : (
              <>
                {messages.map((msg, i) => <MessageBubble key={`msg-${i}-${msg.role}`} msg={msg} />)}
                {loading && (
                  <div className="flex justify-start mb-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mr-2 mt-0.5" style={{ background: BLUE, minWidth: '24px' }}>
                      <Zap size={11} fill="white" color="white" />
                    </div>
                    <div className="rounded-2xl" style={{ background: 'hsl(60,5%,93%)', borderBottomLeftRadius: '6px' }}>
                      <TypingIndicator />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )
          }
        </div>

        {/* Suggestions */}
        {messages.length > 0 && !loading && suggestions.length > 0 && (
          <div className="px-4 pb-2 flex gap-2 flex-wrap shrink-0" style={{ borderTop: '1px solid hsl(0,0%,93%)' }}>
            <p className="text-[10px] text-muted-foreground w-full mt-2 mb-1">Suggestions:</p>
            {suggestions.map(s => (
              <button
                key={s.label}
                onClick={() => onSend(s.message)}
                className="text-xs px-2.5 py-1 rounded-full border transition-colors hover:bg-secondary"
                style={{ border: '1px solid hsl(0,0%,84%)', color: 'hsl(0,0%,38%)', background: 'white' }}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatInputBar({ input, setInput, onSend, loading, suggestions, onOpen }) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
    if (e.key === 'Escape') onOpen(false);
  };

  return (
    <div
      className="w-full flex items-center gap-3 px-5"
      style={{ height: '52px', background: 'white', border: '1px solid hsl(0,0%,88%)', borderRadius: '100px', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}
    >
      <input
        data-testid="global-chat-input"
        value={input}
        onChange={e => setInput(e.target.value)}
        onFocus={() => onOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Ask anything…"
        className="flex-1 text-sm outline-none bg-transparent"
        style={{ color: 'hsl(0,0%,18%)', caretColor: BLUE }}
        disabled={loading}
      />
      {input.trim() ? (
        <button
          data-testid="global-chat-send-button"
          onClick={() => onSend()}
          disabled={loading}
          className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 transition-all active:scale-95"
          style={{ background: BLUE }}
        >
          {loading ? <Loader2 size={14} color="white" className="animate-spin" /> : <Send size={13} color="white" />}
        </button>
      ) : (
        <button
          onClick={() => onSend(suggestions[0]?.message)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs shrink-0 transition-all hover:shadow-sm"
          style={{ background: 'hsl(60,5%,95%)', border: '1px solid hsl(0,0%,85%)', color: 'hsl(0,0%,35%)' }}
        >
          {suggestions[0]?.label || 'List recent todos'}
        </button>
      )}
    </div>
  );
}

// ─── Main component (thin shell — state lives in useChatState) ────────────────────
export default function GlobalChatBar() {
  const inputRef = useRef(null);
  const {
    isOpen, setIsOpen,
    input, setInput,
    messages, loading, suggestions,
    sendMessage, clearChat, QUICK_ACTIONS,
  } = useChatState();

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  return (
    <div className="relative w-full flex justify-center" style={{ maxWidth: '680px', margin: '0 auto', pointerEvents: 'auto' }}>
      <ChatPanel
        isOpen={isOpen}
        messages={messages}
        loading={loading}
        suggestions={suggestions}
        onSend={sendMessage}
        onClear={clearChat}
        onClose={() => setIsOpen(false)}
        QUICK_ACTIONS={QUICK_ACTIONS}
      />
      <ChatInputBar
        input={input}
        setInput={setInput}
        onSend={sendMessage}
        loading={loading}
        suggestions={suggestions}
        onOpen={setIsOpen}
      />
    </div>
  );
}
